"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";

type NavItem = {
    href: string;
    label: string;
    icon: string;
    badge?: string;
};

const mainItems: NavItem[] = [
    { href: "/", label: "Dashboard", icon: "🏠" },
    { href: "/products", label: "Products", icon: "👜" },
    { href: "/inventory", label: "Inventory", icon: "📦" },
    { href: "/orders", label: "Orders", icon: "🧾" },
    { href: "/customers", label: "Customers", icon: "👗" },
    { href: "/suppliers", label: "Suppliers", icon: "🏭" },
];

const utilityItems: NavItem[] = [
    { href: "/reports", label: "Reports", icon: "📊", badge: "Soon" },
    { href: "/settings", label: "Settings", icon: "⚙️", badge: "Soon" },
];

function NavLink({ item, active }: { item: NavItem; active: boolean }) {
    return (
        <Link
            href={item.href}
            className={cn(
                "group flex items-center justify-between rounded-2xl px-4 py-3 text-sm font-medium transition-all",
                active
                    ? "bg-brand-500 text-white shadow-soft"
                    : "text-gray-600 hover:bg-rose-50 hover:text-gray-900"
            )}
        >
            <span className="flex items-center gap-3">
                <span
                    className={cn(
                        "flex h-9 w-9 items-center justify-center rounded-xl text-base transition",
                        active ? "bg-white/20" : "bg-rose-50 group-hover:bg-white"
                    )}
                >
                    {item.icon}
                </span>
                <span>{item.label}</span>
            </span>

            {item.badge ? (
                <span
                    className={cn(
                        "rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide",
                        active ? "bg-white/20 text-white" : "bg-rose-100 text-brand-700"
                    )}
                >
                    {item.badge}
                </span>
            ) : null}
        </Link>
    );
}

export default function Sidebar() {
    const pathname = usePathname();

    return (
        <aside className="w-full border-b border-rose-100 bg-gradient-to-b from-white to-rose-50/70 md:min-h-screen md:w-80 md:border-b-0 md:border-r md:px-4 md:py-5">
            <div className="md:rounded-[28px] md:border md:border-rose-100 md:bg-white md:p-4 md:shadow-soft">
                <div className="px-5 py-5 md:px-3 md:py-3">
                    <div className="rounded-[24px] bg-gradient-to-r from-brand-500 to-rose-400 p-4 text-white shadow-soft">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-white/80">
                                    Bangkok Dress
                                </p>
                                <h2 className="mt-1 text-xl font-bold">Inventory Hub</h2>
                                <p className="mt-2 text-sm text-white/80">
                                    Manage products, orders, and stock in one place.
                                </p>
                            </div>
                            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/20 text-xl backdrop-blur">
                                🛍️
                            </div>
                        </div>
                    </div>
                </div>

                <div className="px-5 pb-4 md:px-3">
                    <div className="mb-3 px-1">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gray-400">
                            Main Menu
                        </p>
                    </div>

                    <nav className="flex gap-2 overflow-x-auto pb-1 md:block md:space-y-2 md:overflow-visible">
                        {mainItems.map((item) => {
                            const active = pathname === item.href;
                            return <NavLink key={item.href} item={item} active={active} />;
                        })}
                    </nav>
                </div>

                <div className="hidden border-t border-rose-100 px-5 pt-4 md:block md:px-3">
                    <div className="mb-3 px-1">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gray-400">
                            Workspace
                        </p>
                    </div>

                    <div className="space-y-2">
                        {utilityItems.map((item) => {
                            const active = pathname === item.href;
                            return <NavLink key={item.href} item={item} active={active} />;
                        })}
                    </div>
                </div>

                <div className="hidden border-t border-rose-100 px-5 py-4 md:block md:px-3">
                    <div className="rounded-2xl bg-rose-50 p-4">
                        <p className="text-sm font-semibold text-gray-900">Today’s focus</p>
                        <p className="mt-1 text-sm text-gray-500">
                            Check low-stock dresses and follow up pending orders before posting new arrivals.
                        </p>
                    </div>
                </div>
            </div>
        </aside>
    );
}